/**
 * Parsing the stored call transcript into playable segments.
 *
 * The transcript is one text blob per call. Recent calls are diarised lines of
 * the form "[MM:SS - MM:SS] text" — real timestamps, so click-to-seek and the
 * playing highlight can be exact. Older calls may be "Name: text" turns or a
 * single unbroken paragraph; those carry no times, so their segments get
 * ESTIMATED starts (character position pro-rated over the audio duration) and
 * are flagged `estimated` so the UI can say so instead of implying precision.
 *
 * Transcripts are PII-redacted with placeholders (PERSON_12, LOCATION_3 …)
 * resolved through the call's AgentMapping. The un-redaction here mirrors
 * InsightPanel.replaceMappedValues so the transcript shows the same names the
 * rest of the page shows.
 */

/** "MM:SS" / "H:MM:SS" → seconds. Returns null for anything else. */
export const clockToSeconds = (value) => {
  if (!value) return null;
  const parts = String(value)
    .trim()
    .split(":")
    .map((p) => Number(p));
  if (parts.some((n) => Number.isNaN(n))) return null;
  if (parts.length === 2) return parts[0] * 60 + parts[1];
  if (parts.length === 3) return parts[0] * 3600 + parts[1] * 60 + parts[2];
  return null;
};

export const secondsToClock = (seconds) => {
  const s = Math.max(0, Math.floor(seconds || 0));
  const m = Math.floor(s / 60);
  return `${String(m).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;
};

export const parseMapping = (raw) => {
  if (!raw) return {};
  try {
    const parsed = typeof raw === "string" ? JSON.parse(raw) : raw;
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
};

/** Same placeholder resolution the insight panel applies to prompt responses. */
export const unredact = (text, mapping) => {
  if (!text || typeof text !== "string") return text || "";
  if (!mapping || Object.keys(mapping).length === 0) return text;

  const resolve = (match, category, id) => {
    const key = `${category.toUpperCase()}_${id}`;
    for (const values of Object.values(mapping)) {
      if (values && typeof values === "object" && values[key]) return values[key];
    }
    const direct = mapping[category.toUpperCase()];
    if (direct && direct[key]) return direct[key];
    return match;
  };

  return text
    .replace(/\b([A-Z]+(?:_[A-Z]+)?)_(\d+)\b/gi, resolve)
    .replace(/\b([A-Z]+(?:_[A-Z]+)?)\s+(\d+)\b/gi, resolve);
};

// A speaker label: "Britney:", "Caller:", or with the tone annotation the
// diariser adds on newer calls — "Agent (tone: professional):". The tone is
// captured separately so the UI can show it as a tag instead of leaving
// "(tone: professional)" glued to the name.
const SPEAKER = "([A-Za-z][\\w .'-]{0,30}?)(?:\\s*\\(tone:\\s*([^)]*)\\))?";

// "[00:12 - 00:27]" optionally followed by a speaker label — the shape every
// recent transcript uses (verified against stored data).
const TIMED_LINE = new RegExp(
  `^\\s*\\[(\\d{1,2}:\\d{2}(?::\\d{2})?)\\s*(?:-|–|to)\\s*(\\d{1,2}:\\d{2}(?::\\d{2})?)?\\]\\s*(?:${SPEAKER}:\\s+)?(.*)$`,
);

const LABELLED_LINE = new RegExp(`^\\s*${SPEAKER}:\\s+(.*)$`);

const cleanTone = (t) => (t && t.trim() ? t.trim().toLowerCase() : null);

/**
 * Stored timestamps cannot be trusted past the first hour. Inspection of real
 * 60+ minute transcripts (prod mn.CallLogs) found, sometimes within a single
 * call: MM:SS with minutes past 59 ("64:19") mixed with H:MM:SS ("1:04:19"),
 * stamps that lose the hour and restart low ("63:59" stored as "3:59"),
 * stamps far beyond the audio ("2:00:00" inside a 74-minute call), and small
 * backwards wobbles ("60:58" then "59:00"). Spoken turns are in order in the
 * text, so order is the ground truth and the clock is repaired against it:
 *
 *   - a stamp beyond the audio's length (when known) is junk → pinned to the
 *     previous turn's time;
 *   - a big backwards jump (> 30 min) is a lost hour → the missing hours are
 *     added back (then sanity-checked against the audio length);
 *   - a small backwards jump is a wobble → pinned to the previous time.
 *
 * Forward-only repairs: a correct, increasing sequence passes through
 * untouched.
 */
const repairTimestamps = (segments, durationSeconds) => {
  const limit =
    durationSeconds > 0 ? durationSeconds + 120 : Number.POSITIVE_INFINITY;
  let offset = 0;
  let prev = 0;
  segments.forEach((seg) => {
    if (seg.start == null) return;
    let s = seg.start + offset;
    if (seg.start > limit) {
      // Hallucinated stamp — beyond the audio even before any offset.
      s = prev;
    } else if (s > limit) {
      // The accumulated offset over-shot (an unwrap met an absolute stamp,
      // e.g. "3:59" followed by "1:04:02"): this stamp is already absolute.
      offset = 0;
      s = Math.max(seg.start, prev);
    } else if (s < prev) {
      const gap = prev - s;
      if (gap > 1800) {
        // Lost hour(s): "63:59" stored as "3:59" restarts the clock low.
        const hours = Math.ceil((gap - 1800) / 3600);
        const unwrapped = s + hours * 3600;
        if (unwrapped <= limit) {
          offset += hours * 3600;
          s = unwrapped;
        } else {
          s = prev;
        }
      } else {
        // Wobble: keep time monotonic without inventing a jump.
        s = prev;
      }
    }
    seg.start = s;
    if (seg.end != null) {
      const e = seg.end + offset;
      seg.end = e >= s && e <= limit ? e : null;
    }
    prev = s;
  });
};

/**
 * @returns {{ segments: Array<{start:number|null,end:number|null,speaker:string|null,text:string}>,
 *             timed: boolean }}
 * `timed` false means every start is an estimate spread over `durationSeconds`.
 */
export const parseTranscript = (raw, mapping = {}, durationSeconds = 0) => {
  const text = unredact(String(raw || "").trim(), mapping);
  if (!text) return { segments: [], timed: false };

  const lines = text.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);

  // Path 1 — timestamped lines. A couple of malformed lines shouldn't demote
  // the whole transcript, so the threshold is "most", not "all"; unmatched
  // lines are folded into the previous segment as continuations.
  const timedMatches = lines.map((l) => l.match(TIMED_LINE));
  const timedCount = timedMatches.filter(Boolean).length;
  if (lines.length > 0 && timedCount >= Math.max(1, lines.length * 0.6)) {
    const segments = [];
    lines.forEach((line, i) => {
      const m = timedMatches[i];
      if (m) {
        segments.push({
          start: clockToSeconds(m[1]),
          end: clockToSeconds(m[2]),
          speaker: m[3] ? m[3].trim() : null,
          tone: cleanTone(m[4]),
          text: m[5] || "",
        });
      } else if (segments.length) {
        segments[segments.length - 1].text += ` ${line}`;
      } else {
        segments.push({ start: 0, end: null, speaker: null, tone: null, text: line });
      }
    });
    repairTimestamps(segments, durationSeconds);
    // A missing end is the next segment's start — better than leaving a hole
    // the highlight would fall through.
    segments.forEach((s, i) => {
      if (s.end == null) s.end = segments[i + 1]?.start ?? null;
    });
    return { segments: segments.filter((s) => s.text.trim()), timed: true };
  }

  // Path 2 — "Name: text" turns, no times.
  const labelled = lines.map((l) => l.match(LABELLED_LINE));
  const labelledCount = labelled.filter(Boolean).length;
  let bare;
  if (lines.length > 1 && labelledCount >= lines.length * 0.6) {
    bare = lines.map((l, i) => {
      const m = labelled[i];
      return m
        ? { speaker: m[1].trim(), tone: cleanTone(m[2]), text: m[3] }
        : { speaker: null, tone: null, text: l };
    });
  } else {
    // Path 3 — an unbroken paragraph. Split into sentence groups of a
    // readable, clickable size. No speakers are invented: the data does not
    // say who spoke, and guessing would present fiction as fact.
    const sentences = text
      .split(/(?<=[.?!])\s+/)
      .map((s) => s.trim())
      .filter(Boolean);
    bare = [];
    let cur = "";
    sentences.forEach((s) => {
      if (cur && (cur.length + s.length > 220 || cur.split(". ").length >= 3)) {
        bare.push({ speaker: null, tone: null, text: cur });
        cur = s;
      } else {
        cur = cur ? `${cur} ${s}` : s;
      }
    });
    if (cur) bare.push({ speaker: null, tone: null, text: cur });
  }

  // Estimated timing: pro-rate by character position. Approximate by nature —
  // flagged so the UI labels the times as estimates.
  const totalChars = bare.reduce((n, s) => n + s.text.length, 0) || 1;
  let consumed = 0;
  const segments = bare.map((s) => {
    const start = durationSeconds > 0 ? (consumed / totalChars) * durationSeconds : null;
    consumed += s.text.length;
    const end = durationSeconds > 0 ? (consumed / totalChars) * durationSeconds : null;
    return { start, end, speaker: s.speaker, tone: s.tone, text: s.text };
  });
  return { segments, timed: false };
};

/**
 * Which side of the call a speaker label belongs to, for colouring and for
 * naming. The diariser labels turns "Agent"/"Caller" (or with names on some
 * calls); anything else is left neutral rather than guessed.
 */
export const speakerRole = (label, agentNames = []) => {
  if (!label) return "other";
  const l = label.trim().toLowerCase();
  if (/^(agent|rep|representative|staff|operator|advisor|counsel(l)?or)\b/.test(l))
    return "agent";
  if (/^(caller|member|patient|client|customer|user)\b/.test(l)) return "caller";
  const first = l.split(/\s+/)[0];
  if (
    agentNames.some((n) => String(n || "").trim().toLowerCase().split(/\s+/)[0] === first)
  )
    return "agent";
  return "other";
};

/**
 * The name shown on a turn. A generic "Agent" label becomes the agent's first
 * name when the call has exactly one agent — the same person the panel header
 * already names — and stays "AGENT" otherwise, because guessing which of two
 * agents spoke would present fiction as fact.
 */
export const speakerDisplayName = (label, role, agentNames = []) => {
  if (!label) return "";
  if (role === "agent" && /^agent\b/i.test(label.trim()) && agentNames.length === 1) {
    const first = String(agentNames[0] || "").trim().split(/\s+/)[0];
    if (first) return first.toUpperCase();
  }
  return label.trim().toUpperCase();
};
